import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Popover,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const CustomDatePicker = ({
  value,
  onChange,
  error,
  placeholder = 'Select Date',
  name,
  max
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);

  // Parse initial date value
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  const parsedDate = parseDate(value);

  // Calendar display state (viewYear and viewMonth)
  const defaultYear = parsedDate ? parsedDate.getFullYear() : new Date().getFullYear();
  const defaultMonth = parsedDate ? parsedDate.getMonth() : new Date().getMonth();

  const [viewYear, setViewYear] = useState(defaultYear);
  const [viewMonth, setViewMonth] = useState(defaultMonth);

  // Sync display state when value changes externally
  useEffect(() => {
    const parsed = parseDate(value);
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [value]);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(triggerRef.current || event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper date calculation functions
  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(prev => prev - 1);
    } else {
      setViewMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(prev => prev + 1);
    } else {
      setViewMonth(prev => prev + 1);
    }
  };

  const handleMonthChange = (e) => {
    setViewMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setViewYear(e.target.value);
  };

  const handleDaySelect = (day) => {
    const formattedMonth = String(viewMonth + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const dateStr = `${viewYear}-${formattedMonth}-${formattedDay}`;
    
    if (onChange) {
      onChange({
        target: {
          name,
          value: dateStr
        }
      });
    }
    handleClose();
  };

  // Formatted date string for input display
  const getDisplayValue = () => {
    if (!value) return '';
    const parts = value.split('-');
    if (parts.length === 3) {
      // Return as MM/DD/YYYY or similar standard format
      return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return value;
  };

  // Generate years list (e.g. 1920 to current year)
  const currentYear = new Date().getFullYear();
  const startYear = 1920;
  const years = [];
  for (let y = currentYear; y >= startYear; y--) {
    years.push(y);
  }

  // Days calculations
  const totalDays = getDaysInMonth(viewYear, viewMonth);
  const firstDayIndex = getFirstDayOfMonth(viewYear, viewMonth);
  const blanks = Array(firstDayIndex).fill(null);
  const days = [];
  for (let d = 1; d <= totalDays; d++) {
    days.push(d);
  }

  const calendarGrid = [...blanks, ...days];

  // Max date verification
  const isDateDisabled = (day) => {
    if (!max) return false;
    const formattedMonth = String(viewMonth + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const currentDateStr = `${viewYear}-${formattedMonth}-${formattedDay}`;
    return currentDateStr > max;
  };

  // Check if a day is today
  const isToday = (day) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === viewMonth &&
      today.getFullYear() === viewYear
    );
  };

  // Check if a day is selected
  const isSelected = (day) => {
    return (
      parsedDate &&
      parsedDate.getDate() === day &&
      parsedDate.getMonth() === viewMonth &&
      parsedDate.getFullYear() === viewYear
    );
  };

  return (
    <>
      {/* Input Trigger */}
      <Box
        ref={triggerRef}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          width: '100%',
          outline: 'none'
        }}
      >
        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CalendarTodayRoundedIcon sx={{ fontSize: '20px', color: '#94a3b8' }} />
        </Box>
        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
        <Box
          component="input"
          readOnly
          name={name}
          placeholder={placeholder}
          value={getDisplayValue()}
          sx={{
            flex: 1,
            padding: '16px',
            border: 'none',
            background: 'transparent',
            fontSize: '15px',
            color: value ? '#334155' : '#94a3b8',
            outline: 'none',
            fontFamily: "'Poppins', sans-serif",
            width: '100%',
            cursor: 'pointer'
          }}
        />
      </Box>

      {/* Calendar Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
              padding: '16px',
              width: '320px',
              fontFamily: "'Poppins', sans-serif",
              overflow: 'visible'
            }
          }
        }}
      >
        {/* Calendar Header with Navigation and Dropdowns */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Select
              value={viewMonth}
              onChange={handleMonthChange}
              size="small"
              sx={{
                fontSize: '13.5px',
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                color: '#1e293b',
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                bgcolor: '#f8fafc',
                borderRadius: '8px',
                px: 0.5,
                height: '32px',
                '& .MuiSelect-select': {
                  py: 0.5,
                  pr: '24px !important',
                  pl: '8px !important'
                }
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      maxHeight: 200,
                      borderRadius: '8px',
                      fontFamily: "'Poppins', sans-serif"
                    }
                  }
                }
              }}
            >
              {MONTHS.map((m, idx) => (
                <MenuItem key={m} value={idx} sx={{ fontSize: '13px', fontFamily: "'Poppins', sans-serif" }}>
                  {m}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={viewYear}
              onChange={handleYearChange}
              size="small"
              sx={{
                fontSize: '13.5px',
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                color: '#1e293b',
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                bgcolor: '#f8fafc',
                borderRadius: '8px',
                px: 0.5,
                height: '32px',
                '& .MuiSelect-select': {
                  py: 0.5,
                  pr: '24px !important',
                  pl: '8px !important'
                }
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: {
                      maxHeight: 200,
                      borderRadius: '8px',
                      fontFamily: "'Poppins', sans-serif"
                    }
                  }
                }
              }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: '13px', fontFamily: "'Poppins', sans-serif" }}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={handlePrevMonth} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: '5px' }}>
              <ChevronLeftRoundedIcon sx={{ fontSize: '18px', color: '#64748b' }} />
            </IconButton>
            <IconButton size="small" onClick={handleNextMonth} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', p: '5px' }}>
              <ChevronRightRoundedIcon sx={{ fontSize: '18px', color: '#64748b' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Days of Week Row */}
        <Grid container spacing={1} sx={{ mb: 1, textAlign: 'center' }}>
          {WEEKDAYS.map((day) => (
            <Grid item key={day} xs={12 / 7}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', fontFamily: "'Poppins', sans-serif" }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Day Grid */}
        <Grid container spacing={1} sx={{ textAlign: 'center' }}>
          {calendarGrid.map((day, idx) => {
            if (day === null) {
              return <Grid item key={`blank-${idx}`} xs={12 / 7} />;
            }

            const disabled = isDateDisabled(day);
            const selected = isSelected(day);
            const today = isToday(day);

            return (
              <Grid item key={`day-${day}`} xs={12 / 7}>
                <Box
                  onClick={() => !disabled && handleDaySelect(day)}
                  sx={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    fontSize: '13px',
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: selected || today ? 700 : 500,
                    cursor: disabled ? 'default' : 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    // Styling states
                    ...(selected && {
                      bgcolor: '#ff502d',
                      color: '#ffffff',
                      '&:hover': {
                        bgcolor: '#e64525'
                      }
                    }),
                    ...(!selected && today && {
                      border: '1.5px solid #ff502d',
                      color: '#ff502d'
                    }),
                    ...(!selected && !today && {
                      color: disabled ? '#cbd5e1' : '#334155',
                      '&:hover': {
                        bgcolor: disabled ? 'transparent' : 'rgba(255, 80, 45, 0.05)',
                        color: disabled ? '#cbd5e1' : '#ff502d'
                      }
                    })
                  }}
                >
                  {day}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Popover>
    </>
  );
};
